import { connect } from "react-redux";

import {
    convertGosEdoAttachment,
    convertToPdfAction,
    downloadFileAction,
    removeAttachmentAction,
    removeGosEdoAttachmentMeta,
} from "../../../../Engine/ActionCreators/AttachmentActionCreators";
import { CandyDispatch } from "../../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../../withControlDebugInfo";
import { withResolvePaths } from "../../../withResolvePaths";
import { withViewModelSelect } from "../../../withViewModelSelect";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { AttachFormat, AttachInfo } from "../../../../../../Common/CandyApiClient/Types/Attachments/Attacments";
import { ConvertationError, ServerError } from "../../../../../../Common/CandyApiClient/CandyApiErrors";
import type { GosEdoPaths, GosEdoChangeablePaths } from "../types";

import { ViewFile } from "./ViewFile";
import { FileError } from "./FileError";
import { ConvertationStatus, ConvertationType } from "./Convertation/Types";

export interface ViewFileContainerProps {
    bindingPath: AbsoluteModelPath;
    attachmentInstance?: string;
    parentPath: AbsoluteModelPath;
    viewModelSelect: ViewModelSelect;
    setConvertationStatus?: (status: ConvertationStatus) => void;
    gosEdoPaths?: GosEdoPaths;
    changeableGosEdoPaths?: GosEdoChangeablePaths;
}

const mapStateToProps = (state: FullFormState, ownProps: ViewFileContainerProps) => {
    const { bindingPath, viewModelSelect } = ownProps;
    // @ts-ignore См. файл ConvertTask и константы KREC_ERROR_CONVERT, они засовываюст в error FileError
    const fields: { value: string; error: FileError[]; warning: FileError[] } = viewModelSelect.pathViewModel(
        state,
        bindingPath
    );
    const { value: id, error: errors = [], warning: warnings = [] } = fields;

    const attachment = state.attachments.byId[id];
    const fileName = attachment?.meta?.fileName ?? "[NO FILE NAME]";
    const warning = warnings[0];
    const error = errors[0];

    return {
        attachment: attachment,
        fileName: fileName,
        error: error,
        warning: warning,
        withGosEdoConversion: !!ownProps.gosEdoPaths,
        isReadOnly: Boolean(state.settings.isReadOnly),
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: ViewFileContainerProps) => {
    const dispatchRemoveAction = async () => {
        const tasks = [];
        tasks.push(dispatch(removeAttachmentAction(ownProps.parentPath, ownProps.attachmentInstance)));
        if (ownProps.changeableGosEdoPaths) {
            tasks.push(dispatch(removeGosEdoAttachmentMeta(ownProps.changeableGosEdoPaths)));
        }
        await Promise.all(tasks);
    };

    const handleGosEdoConversion = async (attachment: Required<AttachInfo>) => {
        const wrongFormat = attachment.meta.format !== AttachFormat.PdfA1_1_4;

        if (ownProps.gosEdoPaths && wrongFormat) {
            try {
                ownProps.setConvertationStatus?.(ConvertationStatus.InProgress);
                await dispatch(convertGosEdoAttachment(attachment, ownProps.gosEdoPaths));
                ownProps.setConvertationStatus?.(ConvertationStatus.Success);
            } catch (e) {
                if (e instanceof ConvertationError) {
                    ownProps.setConvertationStatus?.(ConvertationStatus.Failed);
                }
                if (e instanceof ServerError) {
                    ownProps.setConvertationStatus?.(ConvertationStatus.ServerError);
                }
                await dispatchRemoveAction();
            }
        }
    };

    const handlePdfConversion = async (attachment: Required<AttachInfo>) => {
        const wrongFormat = attachment.meta.format !== AttachFormat.Pdf;

        if (wrongFormat) {
            try {
                ownProps.setConvertationStatus?.(ConvertationStatus.InProgress);
                await dispatch(convertToPdfAction(attachment));
                ownProps.setConvertationStatus?.(ConvertationStatus.Success);
            } catch (e) {
                if (e instanceof ConvertationError) {
                    ownProps.setConvertationStatus?.(ConvertationStatus.Failed);
                }
                if (e instanceof ServerError) {
                    ownProps.setConvertationStatus?.(ConvertationStatus.ServerError);
                }
            }
        }
    };

    const onAttachmentLoaded = async (
        attachment: Required<AttachInfo>,
        convertationType?: ConvertationType
    ): Promise<void> => {
        if (convertationType === ConvertationType.PdfA1_1_4) {
            await handleGosEdoConversion(attachment);
        } else if (convertationType === ConvertationType.Pdf) {
            await handlePdfConversion(attachment);
        }
    };

    return {
        onRemoveAttachment: async () => {
            await dispatchRemoveAction();
            ownProps.setConvertationStatus?.(ConvertationStatus.Idle);
        },
        onFileDownload: (uri: string, fileName: string) => dispatch(downloadFileAction(uri, fileName)),
        onAttachmentLoaded: onAttachmentLoaded,
    };
};

export const ViewFileContainer = withResolvePaths(
    withViewModelSelect(connect(mapStateToProps, mapDispatchToProps)(withControlDebugInfo(ViewFile)))
);
